import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, finalize, switchMap, tap } from 'rxjs/operators';

import {
	CustomerDTO,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';

@Component({
	selector: 'app-customer-popover',
	templateUrl: './customer-popover.component.html',
	styleUrls: ['./customer-popover.component.scss'],
})
export class CustomerPopoverComponent implements OnInit {
	@Input() customerSelected: CustomerDTO = null;
	@Input() placeholder: string = 'Customer Name';
	@Input() customFormFieldClass: string = 'custom-form-field';
	@Input() showCloseIcon: boolean = false;
	@Input() isEnabled: boolean = true;
	@Input() disabledTooltipMessage: string = 'This field is disabled';
	@Input() isMultiselect: boolean = false;
	@Input() customersSelected: Array<CustomerDTO> = [];
	@Input() showCustomerCode: boolean = false;
	@Input() listOnlyWithCustomerCode: boolean = false;

	@Output() setCustomer = new EventEmitter<any>();
	@Output() setCustomersList = new EventEmitter<any>();

	customersList: Array<any> = [];
	isLoadingCustomersList: boolean = false;
	searchText: string = '';

	debouncer: Subject<void> = new Subject<void>();
	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;
	@ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

	IS_NOT_EMPTY = IS_NOT_EMPTY;

	constructor(
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private cargochief: Cargochief
	) {}

	ngOnInit() {
		if (!this.utilService.isNullOrUndefined(this.customerSelected)) {
			this.placeholder = this.customerSelected.name;
		}

		this.debouncer
			.pipe(
				debounceTime(300),
				tap(() => {
					this.isLoadingCustomersList = true;
					this.customersList = [];
				}),
				switchMap(() => {
					const url = this.urlService.getCustomers(
						0,
						25,
						this.searchText,
						this.cargochief.ACCOUNT_ID
					);
					return this.restService.GET(url).pipe(
						finalize(() => {
							this.isLoadingCustomersList = false;
						})
					);
				})
			)
			.subscribe((customers: any) => {
				this.customersList = this.initialiseSelectedCustomers(customers.data['content']);
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!this.isMultiselect) {
			if (IS_NULL_OR_UNDEFINED(this.customerSelected)) {
				this.placeholder = 'Customer Name';
			} else {
				this.placeholder = this.customerSelected.name;
			}
		} else {
			if (IS_NULL_OR_UNDEFINED(this.customersSelected) || this.customersSelected.length == 0) {
				this.placeholder = 'Customer Name';
			} else {
				this.placeholder = this.customersSelected.map((customer) => customer.name).join(', ');
			}
		}
		if (changes?.listOnlyWithCustomerCode && !changes.listOnlyWithCustomerCode.firstChange) {
			this.debouncer.next();
		}
	}

	inputChanged(searchedText: string) {
		if (searchedText.length > 0) {
			this.debouncer.next();
		} else {
			this.loadInitialCustomersList();
		}
	}

	loadInitialCustomersList() {
		const customersList = null; //this.cargochief.getItem(CARGOCHIEF_KEYS.DEFAULT_CUSTOMERS_LIST);
		if (!this.utilService.isNullOrUndefined(customersList)) {
			this.customersList = JSON.parse(customersList);
		} else {
			this.isLoadingCustomersList = true;
			const url = this.urlService.getCustomers(0, 25, '', this.cargochief.ACCOUNT_ID);
			this.restService
				.GET(url)
				.pipe(
					finalize(() => {
						this.isLoadingCustomersList = false;
					})
				)
				.subscribe((customers: any) => {
					this.customersList = this.initialiseSelectedCustomers(customers.data['content']);
					// this.cargochief.setItem({[CARGOCHIEF_KEYS.DEFAULT_CUSTOMERS_LIST] :  this.customersList});
				});
		}
	}

	setSelectedCustomer(customer: CustomerDTO) {
		this.customerSelected = customer;
		this.updatePlaceholderAndClose();
	}

	addNewCustomer(name: string): CustomerDTO {
		let customer = {} as CustomerDTO;
		customer.name = name;
		return customer;
	}

	addCustomer() {
		this.customerSelected = this.addNewCustomer(this.searchText);
		this.updatePlaceholderAndClose();
	}

	updatePlaceholderAndClose() {
		this.placeholder = this.customerSelected.name;
		this.setCustomer.emit(this.customerSelected);
		if (!this.utilService.isNullOrUndefined(this.trigger)) {
			this.trigger.closePopover();
		}
	}

	renderDownArrowIcon(): boolean {
		if (
			!this.showCloseIcon ||
			(this.showCloseIcon && IS_NULL_OR_UNDEFINED(this.customerSelected))
		) {
			return true;
		}
		return false;
	}

	renderCloseArrowIcon(): boolean {
		if (this.showCloseIcon && IS_NOT_NULL_OR_UNDEFINED(this.customerSelected)) {
			return true;
		}
		return false;
	}

	removeSelectedCustomer() {
		this.customerSelected = null;
		this.setCustomer.emit(null);
		this.placeholder = 'Customer Name';
	}

	handleMultiselect(customer: CustomerDTO, checked: boolean) {
		this.customersSelected = this.customersList.filter((customerDTO) => customerDTO.checked);
		this.setCustomersList.emit(this.customersSelected);
	}

	initialiseSelectedCustomers(customersList: Array<any>): Array<any> {
		return customersList
			.filter((customer: CustomerDTO) => {
				if (this.listOnlyWithCustomerCode) {
					return IS_NOT_NULL_OR_UNDEFINED(customer.customerCode);
				} else {
					return true;
				}
			})
			.sort((a, b) => 0 - (a.name.toLowerCase() > b.name.toLowerCase() ? -1 : 1))
			.map((customer) => {
				if (this.isMultiselect) {
					customer.checked = false;
					if (
						IS_NOT_NULL_OR_UNDEFINED(this.customersSelected) &&
						this.customersSelected.length > 0
					) {
						customer.checked = IS_NOT_NULL_OR_UNDEFINED(
							this.customersSelected.find((cstmr) => cstmr.id == customer.id)
						);
					}
				}
				return customer;
			});
	}

	clearMultiSelection() {
		this.customersSelected = [];
		this.customersList = this.customersList.map((customer) => {
			customer.checked = false;
			return customer;
		});
		this.setCustomersList.emit(this.customersSelected);
	}

	focusToSearchInput() {
		setTimeout(() => {
			if (!this.utilService.isNullOrUndefined(this.searchInput)) {
				this.searchInput.nativeElement.focus();
			}
		});
	}
}
